/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Primitive } from 'c3';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicChart } from 'src/core/components/BasicChart';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist, joinClassNames } from 'src/core/themes/luisStylist';
import { ChartDataProps } from 'src/core/types/chart';
import { transformLineChartData } from 'src/core/utils/chartDataTransforms';

require('./lineChartStyles.less');

const messages = defineMessages({
    lineChartEmptyLabel: {
        id: 'core.charts.lineChartEmptyLabel',
        defaultMessage: 'No items'
    }
});

type XAxisType = 'timeseries' | 'category';

export type LineChartProps<X extends Primitive> = ChartDataProps<X, number> & {
    /**
     * The x axis type either time series or category.
     */
    xAxisType: XAxisType;

    /**
     * The color of the line.
     */
    color?: string;
};

const lineChartTheme = getScopedTheme('LineChart');

const { getClassName } = getStylist('LineChart');

const chart = getClassName('Chart', theme => ({
    minWidth: theme.minChartWidth,
    minHeight: theme.minChartHeight
}));

/**
 * The line chart component
 *
 * @params props the properties of the line chart.
 */
export const LineChart = <X extends Primitive>(props: LineChartProps<X>) => {
    const { localize } = useLocalization();
    const {
        xData,
        yData,
        xAxisType,
        color = lineChartTheme.defaultLineColor,
        emptyChartLabel = localize(messages.lineChartEmptyLabel)
    } = props;

    const jsLineChartClassName = 'js-line-chart';
    const configs: c3.ChartConfiguration = {
        data: {
            x: xData.label,
            xFormat: '%Y-%m-%dT%H:%M:%S.%LZ',
            columns: transformLineChartData(xData, yData),
            empty: {
                label: {
                    text: yData.data.length > 0 ? '' : emptyChartLabel
                }
            },
            type: 'line'
        },

        color: { pattern: [color] },

        grid: {
            y: {
                show: true
            }
        },

        axis: {
            x: {
                type: xAxisType,
                padding: { top: 0, bottom: 0 },
                tick: {
                    count: 8,
                    format: tickFormat(xAxisType),
                    outer: false,
                    fit: true,
                    width: 0
                }
            },
            y: {
                min: 0,
                padding: { top: 0, bottom: 0 },
                show: Boolean(yData.data.length),
                tick: {
                    count: 6,
                    format: (d: number) => d.toFixed()
                }
            }
        },

        padding: {
            top: 30,
            left: 30,
            right: 30
        }
    };

    return <BasicChart className={joinClassNames(jsLineChartClassName, chart)} {...configs} />;
};

/**
 * Formating function used in c3
 */
const tickFormat = (xAxisType: XAxisType): ((data: Date) => string | number) => (data: number | Date): string | number => {
    if (xAxisType === 'timeseries') {
        const day: number = (data as Date).getDate();
        const month: string = (data as Date).toLocaleString('en', {
            month: 'short'
        });

        return `${month} ${day}`;
    }

    return data as number;
};
